import { useApi } from '@/api/useApi';
import type { Quality } from '@/api/types';
import {
  Card,
  CardHead,
  ErrorState,
  Loading,
  CountCard,
  Notice,
  Page,
  PageHead,
  Table,
} from '@/components/ui';
import { formatDate } from '@/lib/format';

type Comment = {
  id: string;
  comment: string;
  created_at: string;
  overall_score: number | null;
  recipient_name: string;
};

/**
 * ADM-05. Value trends for coaching, with the specification's three fairness
 * rules made visible rather than merely obeyed:
 *
 *   "not enough information" is counted, never averaged
 *   every aggregate shows its response count
 *   small cohorts are suppressed
 */
export function QualityPage() {
  const quality = useApi<Quality>('/admin/quality');
  const comments = useApi<{ comments: Comment[] }>('/admin/quality/comments');

  if (quality.loading) return <Loading />;
  if (quality.error)
    return <Page><ErrorState message={quality.error.message} onRetry={quality.reload} /></Page>;
  if (!quality.data) return null;

  const { ratings, coverage, cases, notification_delivery: delivery } = quality.data;

  return (
    <Page>
      <PageHead
        title="Quality"
        subtitle="Value trends, response coverage, and service recovery"
      />

      <div className="stack">
        <Notice tone="info" title="These are coaching signals, not a ranking">
          Ratings are private quality input. A low score opens a conversation; it never triggers
          automatic action against a caregiver.
        </Notice>

        <div className="counts">
          <CountCard label="Completed visits"
            value={coverage.completed_visits}
            context="In the period"
          />
          <CountCard label="Ratings submitted"
            value={coverage.submitted_ratings}
            context="Counted separately from visits"
          />
          <CountCard label="Response rate"
            value={
              coverage.response_rate === null
                ? '—'
                : `${Math.round(coverage.response_rate * 100)}%`
            }
            context="A rising average on a falling rate is not an improvement"
          />
          <CountCard label="Cases past target"
            value={cases.breached}
            context={`${cases.open} open · ${cases.resolved} resolved`}
            tone={cases.breached > 0 ? 'urgent' : 'neutral'}
          />
        </div>

        {ratings.suppressed ? (
          <Notice tone="attention" title="Averages are withheld">
            {ratings.suppression_reason ??
              'Too few responses to report an average without identifying respondents.'}
          </Notice>
        ) : null}

        <Card variant="flush">
          <CardHead
            title="FirstLight values"
            actions={<span className="meta">{ratings.response_count} responses</span>}
          />
          <Table columns={['Value', 'Average', 'Scored', 'Not enough information']}>
            {Object.entries(ratings.values).map(([field, entry]) => (
              <tr key={field}>
                <td className="strong">{entry.label}</td>
                <td>
                  {entry.average === null ? (
                    <span className="meta">Withheld</span>
                  ) : (
                    <span className="serif-number">{entry.average.toFixed(2)}</span>
                  )}
                </td>
                <td className="numeric">{entry.scored_count}</td>
                <td className="numeric">
                  {entry.not_enough_information_count}
                  {entry.not_enough_information_count > 0 ? (
                    <span className="meta"> · excluded from the average</span>
                  ) : null}
                </td>
              </tr>
            ))}
            <tr>
              <td className="strong">Overall experience</td>
              <td>
                {ratings.overall.average === null ? (
                  <span className="meta">Withheld</span>
                ) : (
                  <span className="serif-number">{ratings.overall.average.toFixed(2)}</span>
                )}
              </td>
              <td className="numeric">{ratings.overall.scored_count}</td>
              <td className="meta">Optional question</td>
            </tr>
          </Table>
        </Card>

        <Card variant="flush">
          <CardHead
            title="Comment themes"
            actions={<span className="meta">Access to raw comments is audited</span>}
          />
          <Table columns={['Client', 'Comment', 'Overall', 'When']} empty="No comments yet.">
            {(comments.data?.comments ?? []).map((comment) => (
              <tr key={comment.id}>
                <td className="strong">{comment.recipient_name}</td>
                <td>{comment.comment}</td>
                <td className="numeric">{comment.overall_score ?? '—'}</td>
                <td className="meta">{formatDate(comment.created_at)}</td>
              </tr>
            ))}
          </Table>
        </Card>

        <Card variant="flush">
          <CardHead title="Notification delivery" />
          <Table columns={['State', 'Count']} empty="Nothing sent yet.">
            {Object.entries(delivery).map(([state, count]) => (
              <tr key={state}>
                <td className="strong">{state}</td>
                <td className="numeric">{count}</td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
    </Page>
  );
}
